import {
  QUESTION_KINDS,
  type QuestionKind,
} from './shared-question-model-kind.consts';

export interface QuestionKindCapabilities {
  readonly acceptsFreeText: boolean;
  readonly hasOptionList: boolean;
  readonly hasSelectionLimit: boolean;
  readonly supportsOther: boolean;
  readonly supportsFollowUps: boolean;
  readonly supportsLiveDistribution: boolean;
  readonly canBeRequired: boolean;
  readonly ownsContentUrl: boolean;
}

/**
 * The one place both the builder (which authors these flags per kind) and the
 * responder (which answers against them) read a question kind's affordances.
 * `hasOptionList` and `hasSelectionLimit` are deliberately separate: ranking
 * has an option list to reorder but no min/max selection limit.
 */
export const QUESTION_KIND_CAPABILITIES: Readonly<
  Record<QuestionKind, QuestionKindCapabilities>
> = {
  [QUESTION_KINDS.Choice]: {
    acceptsFreeText: false,
    hasOptionList: true,
    hasSelectionLimit: true,
    supportsOther: true,
    supportsFollowUps: true,
    supportsLiveDistribution: true,
    canBeRequired: true,
    ownsContentUrl: false,
  },
  [QUESTION_KINDS.ImageChoice]: {
    acceptsFreeText: false,
    hasOptionList: true,
    hasSelectionLimit: true,
    supportsOther: false,
    // Figma board `427:1002` "Image Choice - Follow-ups" draws them, so the
    // kind's earlier exclusion was the outlier among the option-bearing kinds.
    supportsFollowUps: true,
    supportsLiveDistribution: true,
    canBeRequired: true,
    ownsContentUrl: false,
  },
  [QUESTION_KINDS.Rating]: {
    acceptsFreeText: false,
    hasOptionList: false,
    hasSelectionLimit: false,
    supportsOther: false,
    supportsFollowUps: true,
    supportsLiveDistribution: true,
    canBeRequired: true,
    ownsContentUrl: false,
  },
  [QUESTION_KINDS.Matrix]: {
    acceptsFreeText: false,
    hasOptionList: false,
    hasSelectionLimit: false,
    supportsOther: false,
    supportsFollowUps: true,
    supportsLiveDistribution: false,
    canBeRequired: true,
    ownsContentUrl: false,
  },
  [QUESTION_KINDS.OpenEnded]: {
    acceptsFreeText: true,
    hasOptionList: false,
    hasSelectionLimit: false,
    supportsOther: false,
    supportsFollowUps: true,
    supportsLiveDistribution: false,
    canBeRequired: true,
    ownsContentUrl: false,
  },
  [QUESTION_KINDS.Ranking]: {
    acceptsFreeText: false,
    hasOptionList: true,
    hasSelectionLimit: false,
    supportsOther: false,
    supportsFollowUps: true,
    supportsLiveDistribution: false,
    canBeRequired: true,
    ownsContentUrl: false,
  },
  [QUESTION_KINDS.Message]: {
    acceptsFreeText: false,
    hasOptionList: false,
    hasSelectionLimit: false,
    supportsOther: false,
    supportsFollowUps: false,
    supportsLiveDistribution: false,
    canBeRequired: false,
    ownsContentUrl: false,
  },
  [QUESTION_KINDS.Url]: {
    acceptsFreeText: false,
    hasOptionList: false,
    hasSelectionLimit: false,
    supportsOther: false,
    supportsFollowUps: false,
    supportsLiveDistribution: false,
    canBeRequired: false,
    ownsContentUrl: true,
  },
  [QUESTION_KINDS.Task]: {
    acceptsFreeText: false,
    hasOptionList: false,
    hasSelectionLimit: false,
    supportsOther: false,
    supportsFollowUps: false,
    supportsLiveDistribution: false,
    canBeRequired: false,
    ownsContentUrl: true,
  },
  [QUESTION_KINDS.ContactInfo]: {
    acceptsFreeText: true,
    hasOptionList: false,
    hasSelectionLimit: false,
    supportsOther: false,
    supportsFollowUps: false,
    supportsLiveDistribution: false,
    canBeRequired: true,
    ownsContentUrl: false,
  },
  [QUESTION_KINDS.MobileApp]: {
    acceptsFreeText: false,
    hasOptionList: false,
    hasSelectionLimit: false,
    supportsOther: false,
    supportsFollowUps: false,
    supportsLiveDistribution: false,
    canBeRequired: false,
    ownsContentUrl: false,
  },
};

export function getQuestionKindCapabilities(
  kind: QuestionKind
): QuestionKindCapabilities {
  return QUESTION_KIND_CAPABILITIES[kind];
}
